import {Injectable} from '@angular/core';
import {NewPractice, Practice} from './entities/practice';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {PracticeCollectionResponse} from './entities/practice-collection-response';
import {ItemCreatedResponse} from '../../entities/base/item-created-response';
import {ApiService} from '../base/api.service';
import {ItemUpdatedResponse} from '../../entities/base/item-updated-response';
import {ItemDeletedResponse} from '../../entities/base/item-deleted-response';
import {PracticePractitioner, PracticePractitionerCollectionResponse, PracticeLocation, PracticeLocationCollectionResponse, OrganisationPracticeInvite, PracticeOrganisationMembership} from './entities';
import {PracticeItemResponse} from './entities/practice-item-response';
import {PracticeRequestModel} from './entities/practice-request-model';
import {CreatePracticeLocationRequestModel} from './entities/create-practice-location-request-model';
import {UpdatePracticeLocationRequestModel} from './entities/update-practice-location-request-model';
import {AdminApiService} from '../base/admin-api.service';
import {GenericItemResponse} from '../../entities/base/generic-item-response';
import {GenericCollectionResponse} from '../../entities/base/generic-collection-response';
import {BaseResponse} from '../../entities/base/base-response';

@Injectable({
  providedIn: 'root'
})
export class PracticeRepositoryService {

  constructor(private api: ApiService, private adminApi: AdminApiService) {
  }

  getAllPractices(): Observable<Practice[]> {
    return this.api.get<PracticeCollectionResponse>('/practices')
      .pipe(map(response => response.items));
  }

  addPractice(model: NewPractice): Observable<number> {
    return this.api.post<ItemCreatedResponse>('/practices/setup/practice', model)
      .pipe(map((result) => result.value));
  }

  updatePractice(model: Practice): Observable<boolean> {
    return this.api.post<ItemUpdatedResponse>('/practices/' + model.id, model)
      .pipe(map(result => result.status.toLocaleUpperCase() === 'OK'));
  }

  getAllPracticePractitioners(practiceId: number): Observable<PracticePractitioner[]> {
    return this.api.get<PracticePractitionerCollectionResponse>('/practices/' + practiceId + '/practitioners')
      .pipe(map(response => response.items));
  }

  getAllPracticeLocations(practiceId: number): Observable<PracticeLocation[]> {
    return this.api.get<PracticeLocationCollectionResponse>('/practices/' + practiceId + '/locations')
      .pipe(map(response => response.items));
  }

  // Admin API methods

  adminLoadPractice(): Observable<Practice> {
    return this.adminApi.get<PracticeItemResponse>('/practices')
      .pipe(map(response => response.value));
  }

  adminUpdatePractice(model: PracticeRequestModel): Observable<boolean> {
    return this.adminApi.put<ItemUpdatedResponse>('/practices', model)
      .pipe(map(result => result.status.toLocaleUpperCase() === 'OK'));
  }

  adminLoadLocations(): Observable<PracticeLocation[]> {
    return this.adminApi.get<PracticeLocationCollectionResponse>('/practicelocations')
      .pipe(map(response => response.items));
  }

  adminCreateLocation(model: CreatePracticeLocationRequestModel): Observable<number> {
    return this.adminApi.post<ItemCreatedResponse>('/practicelocations', model)
      .pipe(map(result => result.value));
  }

  adminUpdateLocation(locationId: number, model: UpdatePracticeLocationRequestModel): Observable<boolean> {
    return this.adminApi.put<ItemUpdatedResponse>('/practicelocations/' + locationId, model)
      .pipe(map(result => result.status.toLocaleUpperCase() === 'OK'));
  }

  adminDeleteLocation(locationId: number): Observable<boolean> {
    return this.adminApi.delete<ItemDeletedResponse>('/practicelocations/' + locationId)
      .pipe(map(result => result.status.toLocaleUpperCase() === 'OK'));
  }

  adminLoadPractitioners(): Observable<PracticePractitioner[]> {
    return this.adminApi.get<PracticePractitionerCollectionResponse>('/practicepractitioners')
      .pipe(map(result => result.items));
  }

  adminAddPractitioner(model: any): Observable<number> {
    return this.adminApi.post<ItemCreatedResponse>('/practicepractitioners', model)
      .pipe(map(result => result.value));
  }

  adminDeletePractitioner(practitionerId: number): Observable<boolean> {
    return this.adminApi.delete<ItemDeletedResponse>('/practicepractitioners/' + practitionerId)
      .pipe(map(result => result.status.toLocaleUpperCase() === 'OK'));
  }

  // Organisation invites (practice-scoped — X-Practice-Id header via interceptor)

  getInviteCode(): Observable<string> {
    return this.api.get<GenericItemResponse<{inviteCode: string}>>('/practices/invite-code')
      .pipe(map(res => res.value.inviteCode));
  }

  regenerateInviteCode(): Observable<string> {
    return this.api.post<GenericItemResponse<{inviteCode: string}>>('/practices/invite-code/regenerate')
      .pipe(map(res => res.value.inviteCode));
  }

  getOrganisationInvites(): Observable<GenericCollectionResponse<OrganisationPracticeInvite>> {
    return this.api.get<GenericCollectionResponse<OrganisationPracticeInvite>>('/practices/organisation-invites');
  }

  approveOrganisationInvite(inviteId: number, isManaged: boolean, practiceLocationIds: number[]): Observable<BaseResponse> {
    return this.api.post<BaseResponse>(`/practices/organisation-invites/${inviteId}/approve`, {isManaged, practiceLocationIds});
  }

  rejectOrganisationInvite(inviteId: number): Observable<BaseResponse> {
    return this.api.post<BaseResponse>(`/practices/organisation-invites/${inviteId}/reject`);
  }

  getOrganisationMemberships(): Observable<GenericCollectionResponse<PracticeOrganisationMembership>> {
    return this.api.get<GenericCollectionResponse<PracticeOrganisationMembership>>('/practices/organisations');
  }

  updateOrganisationAccessLevel(organisationId: number, isManaged: boolean): Observable<BaseResponse> {
    return this.api.request<BaseResponse>('PATCH', `/practices/organisations/${organisationId}/access-level`, {body: {isManaged}});
  }
}
